// @ts-strict-ignore
import React, { useEffect, useState } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import window from '../window'
import {
  Card,
  Field,
  FieldGroup,
  Label,
  Input,
  Select,
  Button,
  RadioGroup,
  Radio,
} from '../../../../atoms'
import Callout from '../../../callout'
import { calculation } from './calculation.ts'
import { lineOptions } from './line-options.ts'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

const formatThousands = (n) => n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')

const PensionsCalculator = ({
  params: { age: ageParam, retirement: retirementParam, income: incomeParam },
  warningText,
}) => {
  const [age, setAge] = useState(ageParam)
  const [retirementAge, setRetirementAge] = useState(retirementParam)
  const [income, setIncome] = useState(incomeParam)
  const [pensionPot, setPensionPot] = useState(Math.floor((incomeParam * 2) / 10000) * 10000)
  const [targetAnnualIncome, setTargetAnnualIncome] = useState(incomeParam)
  const [monthlyContribution, setMonthlyContribution] = useState(
    Math.ceil(incomeParam / 12000) * 50,
  )
  const [monthlyContributionPercentage, setMonthlyContributionPercentage] = useState(null)
  const [grossMonthlySalary, setGrossMonthlySalary] = useState(incomeParam)
  const [employerMonthlyContribution, setEmployerMonthlyContribution] = useState(
    Math.ceil(incomeParam / 12000) * 50,
  )
  const [employerMonthlyContributionPercentage, setEmployerMonthlyContributionPercentage] =
    useState(0)
  const [includeFullStatePension, setIncludeFullStatePension] = useState(true)
  const [take25PercentTaxFree, setTake25PercentTaxFree] = useState(false)
  const [inflation, setInflation] = useState(2)
  const interest = 3
  const [chartData, setChartData] = useState(null)

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location?.search)
    const entries = Object.fromEntries(urlSearchParams.entries())
    const entriesParsed = Object.entries(entries).reduce(
      (obj, [key, val]) => ({ ...obj, [key]: parseInt(val) }),
      {},
    )
    setAge(entriesParsed.age ?? ageParam)
    setRetirementAge(entriesParsed.retirement ?? retirementParam)
    setIncome(entriesParsed.income ?? incomeParam)
  }, [ageParam, retirementParam, incomeParam])

  useEffect(() => {
    setPensionPot(Math.floor((income * 2) / 10000) * 10000)
    setTargetAnnualIncome(income)
    setMonthlyContribution(Math.ceil(income / 12000) * 50)
    setGrossMonthlySalary(income)
    setEmployerMonthlyContribution(Math.ceil(income / 12000) * 50)
  }, [income])

  function calculatePensionPayout() {
    const totalMonthlyContribution =
      monthlyContributionPercentage === null
        ? monthlyContribution + employerMonthlyContribution
        : (grossMonthlySalary *
            (monthlyContributionPercentage + employerMonthlyContributionPercentage)) /
          100

    setChartData(
      calculation({
        age,
        retirementAge,
        pensionPot,
        targetAnnualIncome,
        monthlyContribution: totalMonthlyContribution,
        includeFullStatePension,
        take25PercentTaxFree,
        inflation,
        interest,
      }),
    )
  }

  return (
    <div className='PensionsCalculator Calculator'>
      <Card variant='plum'>
        <div className='inner-card'>
          <Field stack={true}>
            <Label>Age</Label>
            <Input value={age} onChange={(e) => setAge(e.target.value)} type='number' />
          </Field>

          <Field stack={true}>
            <Label>Retirement age</Label>
            <Input
              value={retirementAge}
              onChange={(e) => setRetirementAge(e.target.value)}
              type='number'
            />
          </Field>

          <Field stack={true}>
            <Label>Target annual income</Label>
            <Input
              value={targetAnnualIncome}
              onChange={(e) => setTargetAnnualIncome(e.target.value)}
              type='text'
              prefix='£'
              format='thousands'
            />
          </Field>
        </div>

        <h2>Get a more accurate estimate by answering these questions</h2>

        <Field stack={true}>
          <Label>Personal monthly contribution</Label>
          <FieldGroup type='first-small'>
            <Select
              name='personal-monthly-contribution'
              value={monthlyContributionPercentage === null ? 'pounds' : 'percentage'}
              options={[
                { value: 'pounds', text: '£' },
                { value: 'percentage', text: '%' },
              ]}
              onChange={(e) => {
                if (e.target.value === 'pounds') {
                  setMonthlyContributionPercentage(null)
                } else if (e.target.value === 'percentage') {
                  setMonthlyContributionPercentage(10)
                }
              }}
            />
            <Input
              value={
                monthlyContributionPercentage === null
                  ? monthlyContribution
                  : monthlyContributionPercentage
              }
              onChange={(e) => {
                if (monthlyContributionPercentage === null) {
                  setMonthlyContribution(e.target.value)
                } else {
                  setMonthlyContributionPercentage(e.target.value)
                }
              }}
              type={monthlyContributionPercentage === null ? 'text' : 'number'}
              prefix={monthlyContributionPercentage === null ? '£' : null}
              suffix={monthlyContributionPercentage === null ? null : '%'}
              format={monthlyContributionPercentage === null ? 'thousands' : null}
            />
          </FieldGroup>
        </Field>

        {monthlyContributionPercentage !== null && (
          <Field stack={true}>
            <Label>Monthly salary before tax</Label>
            <Input
              value={grossMonthlySalary}
              onChange={(e) => setGrossMonthlySalary(e.target.value)}
              type='text'
              prefix='£'
              format='thousands'
            />
          </Field>
        )}

        <Field stack={true}>
          <Label>Employer monthly contribution</Label>
          <Input
            value={
              monthlyContributionPercentage === null
                ? employerMonthlyContribution
                : employerMonthlyContributionPercentage
            }
            onChange={(e) => {
              if (monthlyContributionPercentage === null) {
                setEmployerMonthlyContribution(e.target.value)
              } else {
                setEmployerMonthlyContributionPercentage(e.target.value)
              }
            }}
            type={monthlyContributionPercentage === null ? 'text' : 'number'}
            prefix={monthlyContributionPercentage === null ? '£' : null}
            suffix={monthlyContributionPercentage === null ? null : '%'}
            format={monthlyContributionPercentage === null ? 'thousands' : null}
          />
        </Field>

        <Field stack={true}>
          <Label>Current pension pot</Label>
          <Input
            value={pensionPot}
            onChange={(e) => setPensionPot(e.target.value)}
            type='text'
            prefix='£'
            format='thousands'
          />
        </Field>

        <Field stack={true} style={{ marginTop: 32 }}>
          <Label>Include full state pension?</Label>
          <RadioGroup>
            <Radio
              value={'true'}
              name='include-full-state-pension'
              currentValue={includeFullStatePension.toString()}
              onChange={(e) => {
                setIncludeFullStatePension(e.target.value === 'true')
              }}
            >
              Yes
            </Radio>
            <Radio
              name='include-full-state-pension'
              value={'false'}
              currentValue={includeFullStatePension.toString()}
              onChange={(e) => {
                setIncludeFullStatePension(e.target.value === 'true')
              }}
            >
              No
            </Radio>
          </RadioGroup>
        </Field>

        <Field stack={true}>
          <Label>Take 25% tax free at 55(57)?</Label>
          <RadioGroup>
            <Radio
              value={'true'}
              currentValue={take25PercentTaxFree.toString()}
              onChange={(e) => {
                setTake25PercentTaxFree(e.target.value === 'true')
              }}
            >
              Yes
            </Radio>
            <Radio
              value={'false'}
              currentValue={take25PercentTaxFree.toString()}
              onChange={(e) => {
                setTake25PercentTaxFree(e.target.value === 'true')
              }}
            >
              No
            </Radio>
          </RadioGroup>
        </Field>

        <Field stack={true}>
          <Label>Inflation (%)</Label>
          <FieldGroup>
            <Input
              value={inflation}
              onChange={(e) => setInflation(e.target.value)}
              type={'number'}
            />
          </FieldGroup>
        </Field>

        <Button
          variant={chartData === null ? 'primary' : 'secondary'}
          onClick={calculatePensionPayout}
          style={{ marginTop: 8 }}
        >
          {chartData === null ? 'Calculate payout' : 'Recalculate payout'}
        </Button>

        {chartData !== null && (
          <div className='pensions-chart'>
            <h3>
              You&apos;re set to have{' '}
              <span>£{formatThousands(Math.round(chartData.retirementPot))}</span> when you retire.
            </h3>
            <h3>
              If you take <span>£{formatThousands(chartData.yearlyWithdrawal)}</span> a year or{' '}
              <span>£{formatThousands(chartData.monthlyWithdrawal)}</span> a month, this will last
              you until you turn <span>{chartData.finalPayAge}</span>.
            </h3>
            <div className='pensions-chart-graph'>
              <Line
                options={lineOptions([retirementAge - age])}
                data={{
                  labels: chartData.ages.map((a) => `Age ${a}`),
                  datasets: [
                    {
                      label: 'Actual pension',
                      data: chartData.pensionPotData,
                      borderColor: '#DB4D75',
                      backgroundColor: '#DB4D75',
                      spanGaps: true,
                    },
                  ],
                }}
              />
            </div>
          </div>
        )}
      </Card>
      <Callout variant='platinum-warning' bold={true} text={warningText} />
    </div>
  )
}

export default PensionsCalculator
